'use client';

import React, { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import Request from '@/services/util/request-service';
import ErrorLoggerService from '@/services/util/error-logger-service';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}
const reportedCompErrors = new Set<string>();

class CompErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.logErrorToServer(error, errorInfo);
  }

  logErrorToServer(error: Error, errorInfo: ErrorInfo) {
    const origin = window.location.origin;
    if (origin.includes('localhost') || origin === 'https://127.0.0.1:4200') return;
    const hash = `${error.message}:${error.stack}`;
    if (reportedCompErrors.has(hash)) return;

    reportedCompErrors.add(hash);

    Request.post('log/error', {
      error_type: 'COMP_BOUNDARY',
      message: error.message,
      subdomain_origin: origin,
      stacktrace: error?.stack || '{}',
      source: 'frontend',
      severity: 'error',
      user_agent: navigator.userAgent,
    }).catch(console.error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <>
          <div className='flex flex-col p-10 m-10'>
            <h2 className={`text-text_gray_800`}>Something went wrong.</h2>
            <span className={`text-md text-text_gray_800`}>Try reloading the page again.</span>
          </div>
        </>
      );
    }
    return this.props.children;
  }
}

export const ErrorBoundary = ({ children }: { children: ReactNode }) => {
  useEffect(() => {
    // Setup global window.onerror, window.onunhandledrejection, etc.
    ErrorLoggerService.setupGlobalErrorHandlers();
  }, []);

  return <CompErrorBoundary>{children}</CompErrorBoundary>;
};
